import type {
  LogRecord,
  LogTransport,
  TrackingConsent,
} from '@funkit/connect-core'

import { serializeLogBatch } from './serializeLogBatch'

/**
 * React Native `LogTransport` that ships records to Datadog over **raw HTTP**
 * (`fetch`) — deliberately NOT the Datadog SDK. `@datadog/mobile-react-native`
 * pulls in native modules (autolinking, pods, no Expo Go); a plain `fetch` to
 * the logs intake needs none, so this works in Expo Go and any RN runtime.
 *
 * Records are batched and flushed fire-and-forget on a timer (or when the batch
 * fills); network errors are swallowed so logging never breaks a user flow.
 * Authenticates with the public Datadog **client token** via `dd-api-key`
 * (the same credential the browser SDK uses — safe to embed), so no server-side
 * API key is needed.
 */
export interface DatadogHttpLogTransportOptions {
  /** Datadog client token (`pub…`). Safe to ship in client apps. */
  clientToken: string
  /** Service name attached to every log. */
  service?: string
  /** Datadog site, e.g. `datadoghq.com` (default), `datadoghq.eu`. */
  site?: string
  /** Initial environment tag; also settable later via {@link setEnv}. */
  env?: string
  /** Flush the batch at most this often (ms). Default 5000. */
  flushIntervalMs?: number
  /** Flush immediately once the batch reaches this size. Default 50. */
  maxBatchSize?: number
}

interface DatadogLogEntry {
  ddsource: string
  service: string
  status: LogRecord['level']
  message: string
  ddtags?: string
  error?: { message: string; stack?: string; kind: string }
  [attribute: string]: unknown
}

const DEFAULT_SERVICE = 'connect-rn'
const DEFAULT_SITE = 'datadoghq.com'
const DEFAULT_FLUSH_INTERVAL_MS = 5_000
const DEFAULT_MAX_BATCH_SIZE = 50

export class DatadogHttpLogTransport implements LogTransport {
  private readonly intakeUrl: string
  private readonly service: string
  private readonly flushIntervalMs: number
  private readonly maxBatchSize: number

  private env: string | undefined
  private consent: TrackingConsent = 'granted'
  private queue: DatadogLogEntry[] = []
  private flushTimer: ReturnType<typeof setTimeout> | null = null

  constructor(options: DatadogHttpLogTransportOptions) {
    const site = options.site ?? DEFAULT_SITE
    this.service = options.service ?? DEFAULT_SERVICE
    this.env = options.env
    this.flushIntervalMs = options.flushIntervalMs ?? DEFAULT_FLUSH_INTERVAL_MS
    this.maxBatchSize = options.maxBatchSize ?? DEFAULT_MAX_BATCH_SIZE
    // Browser/mobile logs intake; the client token authenticates via dd-api-key.
    this.intakeUrl =
      `https://browser-intake-${site}/api/v2/logs` +
      `?dd-api-key=${encodeURIComponent(options.clientToken)}` +
      `&ddsource=react-native&dd-evp-origin=react-native`
  }

  setEnv(environment: string): void {
    this.env = environment
  }

  setTrackingConsent(consent: TrackingConsent): void {
    this.consent = consent
    if (consent !== 'granted') {
      // Drop anything buffered when consent is withdrawn.
      this.queue = []
      this.clearTimer()
    }
  }

  log({ level, title, context, error }: LogRecord): void {
    if (this.consent !== 'granted') {
      return
    }

    const entry: DatadogLogEntry = {
      // Caller context first so the mandatory Datadog fields below always win —
      // a future LogContext attribute named e.g. `message`/`status` can't
      // silently corrupt the record.
      ...context,
      ddsource: 'react-native',
      service: this.service,
      status: level,
      message: title,
      ...(this.env ? { ddtags: `env:${this.env}` } : {}),
      ...(error
        ? {
            error: {
              message: error.message,
              stack: error.stack,
              kind: error.name,
            },
          }
        : {}),
    }

    this.queue.push(entry)

    if (this.queue.length >= this.maxBatchSize) {
      this.flush()
    } else {
      this.scheduleFlush()
    }
  }

  /** Send the buffered batch. Fire-and-forget; network errors are swallowed. */
  flush(): void {
    this.clearTimer()
    if (this.queue.length === 0) {
      return
    }
    if (typeof fetch !== 'function') {
      // No fetch (e.g. SSR import) — drop rather than throw.
      this.queue = []
      return
    }

    const batch = this.queue
    this.queue = []

    fetch(this.intakeUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: serializeLogBatch(batch),
    }).catch(() => {
      // Swallow: logging must never break a user flow.
    })
  }

  private scheduleFlush(): void {
    if (this.flushTimer != null) {
      return
    }
    this.flushTimer = setTimeout(() => {
      this.flushTimer = null
      this.flush()
    }, this.flushIntervalMs)
  }

  private clearTimer(): void {
    if (this.flushTimer != null) {
      clearTimeout(this.flushTimer)
      this.flushTimer = null
    }
  }
}
